import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import i18n from '@app/i18n'

import { AddressApi } from '@entities/Address'

import { InfoModal, useModalStore } from '@features/modal'
import styles from '@features/modal/ui/styles.module.scss'
import { AddressItemType } from '@features/shipping-methods/types'

import { useSlotStore } from '@widgets/slots'



import { client } from '@shared/api/client'
import { SHOP_ID } from '@shared/contants'
import {
  CustomerAddressInput,
  GeoPointInput,
  ZonesForGeoPointQuery,
} from '@shared/generated'




const addressApi = new AddressApi(client)

const modalStore = useModalStore.getState()

const t = i18n.t

const openErrorInfoModal = (errorTransKey?: string) => {
  modalStore.openModal('info', <InfoModal />, {
    title: t('common:modal.error.title'),
    description:
      t(`errors:${errorTransKey}`) ?? t('common:modal.error.description'),
    classes: {
      content: styles['error-info-modal-content'],
    },
  })
}

export type SaveAddressActionProps = {
  addressInfo: CustomerAddressInput;
  geoPoint?: GeoPointInput;
};

interface AddressState {
  selectedAddress: AddressItemType | null;
  savedAddresses: AddressItemType[];
  deliveryType: 'pickup' | 'delivery' | null;
  shopId: string;
  zoneId: string | null;
  hasHydrated: boolean;
  zones: ZonesForGeoPointQuery['zonesForGeoPoint'];
  isCorrectSearchAddress: boolean;
  setHasHydrated: (value: boolean) => void;
  setSelectedAddress: (address: AddressItemType) => void;
  setDeliveryType: (type: 'pickup' | 'delivery' | null) => void;
  addAddress: (address: AddressItemType) => void;
  removeAddress: (id: string) => void;
  clearSelectedAddress: () => void;
  isPickup: () => boolean;
  isActiveAddress: (id: string | null | undefined) => boolean;
  setShopId: (shopId: string) => void;
  setZoneId: (zoneId: string) => void;
  clearSavedAddresses: () => void;
  saveAddress: (dto: SaveAddressActionProps) => void;
  deleteAddress: (id: string) => Promise<void>;
}

export const useAddressStore = create(
  persist<AddressState>(
    (set, get) => ({
      selectedAddress: null,
      savedAddresses: [],
      deliveryType: null,
      shopId: SHOP_ID,
      zoneId: null,
      hasHydrated: false,
      // TODO: Zones
      zones: null,
      isCorrectSearchAddress: false,

      setHasHydrated: (value) => set({ hasHydrated: value }),

      setDeliveryType: (type) => set({ deliveryType: type }),

      setSelectedAddress: (address) => {
        const shopId = address?.shop_id || SHOP_ID

        const clearSelectedSlot = useSlotStore.getState().clearSelectedSlot
        clearSelectedSlot()

        set({
          selectedAddress: address,
          shopId,
        })
      },

      setShopId: (shopId: string) => set({ shopId }),

      setZoneId: (zoneId: string) => set({ zoneId }),

      addAddress: (address) => {
        const { savedAddresses } = get()
        set({
          savedAddresses: [ ...savedAddresses, address ],
        })
      },

      removeAddress: (id) => {
        const { savedAddresses, selectedAddress } = get()
        const updated = savedAddresses.filter((a) => a.id !== id)
        const isSelectedRemoved = selectedAddress?.id === id

        set({
          savedAddresses: updated,
          selectedAddress: isSelectedRemoved ? null : selectedAddress,
          deliveryType: isSelectedRemoved ? null : get().deliveryType,
          shopId: isSelectedRemoved ? SHOP_ID : get().shopId,
        })
      },

      clearSelectedAddress: () => {
        set({
          selectedAddress: null,
          deliveryType: null,
          shopId: SHOP_ID,
        })
      },

      clearSavedAddresses: () => set({ savedAddresses: [] }),

      isPickup: () => get().deliveryType === 'pickup',

      isActiveAddress: (id) => {
        const currentId = get().selectedAddress?.id
        return Boolean(id && currentId && id === currentId)
      },

      saveAddress: async ({
        addressInfo,
        geoPoint,
      }: SaveAddressActionProps) => {
        try {
          const res = await addressApi.saveAddress(addressInfo)

          const newAddress = res.data?.saveAddress as AddressItemType

          if (newAddress) {
            get().setSelectedAddress({
              ...newAddress,
              customerGeoPoint: {
                ...geoPoint,
                __typename: 'GeoPoint',
              },
            })

            client.cache.modify({
              id: client.cache.identify({ __typename: 'Query' }),
              fields: {
                myAddresses (existing = []) {
                  return [ ...existing, newAddress ]
                },
              },
            })
          }
        }
        catch (error) {
          console.error('saveAddress error:', error)
          openErrorInfoModal('save_address')
        }
      },

      deleteAddress: async (id) => {
        try {
          await addressApi.deleteAddress(id)
          get().removeAddress(id)
          useSlotStore.getState().clearSelectedSlot()
        }
        catch (error) {
          console.error('deleteAddress error:', error)
          openErrorInfoModal('delete_address')
        }
      },
    }),
    {
      name: 'address-store',
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    },
  ),
)
